import {
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
} from "@mui/material";
import { type JSX } from "react";
import { BTX_DEPENDENT_RATE_BASIS } from "@platform/feature-flags";
import { FeatureFlagBoundary } from "@platform/fe-utils";

import { RateScheduleFormData } from "../../types/models";
import {
  isBasicLifeCoverageType,
  isLifeCoverageType,
} from "../../utils/rate-helpers";

import { AdndRateConfigSection } from "./AdndRateConfigSection";
import { DependentRateUnitSection } from "./DependentRateUnitSection";
import { RateUnitSection } from "./RateUnitSection";

interface AgeBandedRateSectionProps {
  formData: RateScheduleFormData;
  setFormData: React.Dispatch<React.SetStateAction<RateScheduleFormData>>;
  coverageType?: string;
}

/**
 * Age-banded dependent rate controls: whether to include dependent rates,
 * whether dependents are combined or separate, rate unit (volume/unit) configuration,
 * and AD&D configuration for life products. Extracted to reduce RatesTab cyclomatic complexity.
 */
export function AgeBandedRateSection({
  formData,
  setFormData,
  coverageType,
}: AgeBandedRateSectionProps): JSX.Element {
  return (
    <>
      <FormControl component="fieldset">
        <FormLabel component="legend">Are Dependent Rates Included?</FormLabel>
        <RadioGroup
          row
          value={formData.includeDependentRates === true ? "yes" : "no"}
          onChange={(e) => {
            const include = e.target.value === "yes";
            setFormData((prev: RateScheduleFormData) => ({
              ...prev,
              includeDependentRates: include,
              dependentRateType: include ? "combined" : undefined,
            }));
          }}
        >
          <FormControlLabel
            value="yes"
            control={<Radio color="brandPrimary" />}
            label="Yes"
          />
          <FormControlLabel
            value="no"
            control={<Radio color="brandPrimary" />}
            label="No"
          />
        </RadioGroup>
      </FormControl>

      {formData.includeDependentRates && (
        <FormControl component="fieldset">
          <FormLabel component="legend">
            Are dependents combined or separate?
          </FormLabel>
          <RadioGroup
            row
            value={formData.dependentRateType || ""}
            onChange={(e) => {
              setFormData((prev: RateScheduleFormData) => ({
                ...prev,
                dependentRateType: e.target.value as "combined" | "separate",
              }));
            }}
          >
            <FormControlLabel
              value="combined"
              control={<Radio color="brandPrimary" />}
              label="Combined"
            />
            <FormControlLabel
              value="separate"
              control={<Radio color="brandPrimary" />}
              label="Separate"
            />
          </RadioGroup>
        </FormControl>
      )}

      {/* ─── Rates based on volume or unit? ─── */}
      <RateUnitSection
        formData={formData}
        setFormData={setFormData}
        coverageType={coverageType}
      />

      {/* ─── Dependent rates based on volume or unit? (Basic Life only, when dependents included) ─── */}
      {formData.includeDependentRates &&
        isBasicLifeCoverageType(coverageType) && (
          <FeatureFlagBoundary flag={BTX_DEPENDENT_RATE_BASIS}>
            <DependentRateUnitSection
              formData={formData}
              setFormData={setFormData}
            />
          </FeatureFlagBoundary>
        )}

      {/* ─── AD&D Rate Configuration (life products only) ─── */}
      {isLifeCoverageType(coverageType) && (
        <AdndRateConfigSection
          formData={formData}
          setFormData={setFormData}
          coverageType={coverageType}
        />
      )}
    </>
  );
}
